import type {ReactNode} from 'react';
import {ArrowRight, BookOpen, Cpu, Scale, ShieldCheck, Sparkles} from 'lucide-react';

export const editorialAuthor = {
  name: 'Rodrigo Augustus',
  slug: '/autor/rodrigo-augustus/',
  image: '/guisite/rodrigo-augustus-portrait-v1.webp',
  role: 'Editor de tecnologia e decisões de compra',
  description:
    'Pseudônimo editorial de um profissional formado em Direito, apaixonado por eletrônicos, inteligência artificial e escolhas de compra bem fundamentadas.',
};

export function EditorialAuthorLink() {
  return (
    <a className="article-author-link" href={editorialAuthor.slug} rel="author">
      Por {editorialAuthor.name}
    </a>
  );
}

export function AuthorPage({header, footer}: {header: ReactNode; footer: ReactNode}) {
  return (
    <main className="site-shell author-shell">
      {header}
      <article className="author-page">
        <header className="author-hero">
          <figure className="author-avatar">
            <img
              src={editorialAuthor.image}
              alt="Retrato editorial de Rodrigo Augustus"
              width={1122}
              height={1402}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </figure>
          <div>
            <a className="breadcrumb" href="/">GuiaSite / Autor</a>
            <p className="section-kicker">Responsabilidade editorial</p>
            <h1>Rodrigo Augustus</h1>
            <p className="author-role">{editorialAuthor.role}</p>
            <p className="author-intro">
              Rodrigo Augustus é o pseudônimo editorial adotado pelo responsável pelo GuiaSite.
              Formado em Direito e apaixonado por eletrônicos e inteligência artificial, ele
              combina pesquisa documental, comparação de evidências e leitura crítica para
              transformar especificações em decisões de compra mais claras.
            </p>
            <div className="author-signals" aria-label="Áreas de atuação">
              <span><Scale size={17} /> Formação em Direito</span>
              <span><Cpu size={17} /> Eletrônicos e IA</span>
              <span><ShieldCheck size={17} /> Consumo responsável</span>
            </div>
          </div>
        </header>

        <div className="author-content">
          <section>
            <p className="section-kicker">O que orienta as análises</p>
            <h2>Comprar melhor exige contexto, não uma pilha de especificações.</h2>
            <p>
              O trabalho editorial começa pela pergunta que realmente muda a escolha: para quem
              o produto faz sentido, em qual faixa de preço e diante de quais alternativas.
              Garantia, política de atualizações, privacidade, custos recorrentes, restrições
              contratuais e risco de arrependimento recebem o mesmo cuidado que tela,
              desempenho, áudio ou bateria.
            </p>
          </section>

          <section className="author-principles" aria-label="Princípios editoriais">
            <div>
              <BookOpen aria-hidden="true" />
              <h3>Fontes verificáveis</h3>
              <p>Documentação oficial, manuais, políticas públicas e referências técnicas identificadas no texto.</p>
            </div>
            <div>
              <Sparkles aria-hidden="true" />
              <h3>Experiência declarada</h3>
              <p>O uso próprio só é apresentado como experiência quando realmente ocorreu, com seus limites explicados.</p>
            </div>
            <div>
              <Scale aria-hidden="true" />
              <h3>Independência editorial</h3>
              <p>Links de afiliados ajudam a financiar o site, mas entram depois do veredito e não compram recomendação.</p>
            </div>
          </section>

          <section className="author-disclosure">
            <p className="section-kicker">Por que um pseudônimo?</p>
            <h2>Privacidade sem esconder responsabilidade.</h2>
            <p>
              O pseudônimo separa a identidade civil da atividade pública, mas não altera o
              compromisso com autoria, correções e transparência. Cada conteúdo assinado por
              Rodrigo Augustus segue a metodologia pública do GuiaSite e pode ser revisto quando
              surgem novos dados, mudanças de preço ou correções relevantes.
            </p>
            <a href="/metodologia/guiasite-score/">
              Conhecer a metodologia GuiaSite
              <ArrowRight size={17} aria-hidden="true" />
            </a>
          </section>

          <section className="author-desk">
            <div>
              <span>01</span>
              <p className="section-kicker">Decisões de compra</p>
              <h2>Eletrônicos para uso real</h2>
              <p>Celulares, consoles, monitores, tablets e serviços analisados por perfil, longevidade e custo total.</p>
              <a href="/celulares/">Explorar decisões <ArrowRight size={16} /></a>
            </div>
            <div>
              <span>02</span>
              <p className="section-kicker">Tecnologia para criar</p>
              <h2>Equipamentos para criadores</h2>
              <p>Áudio, imagem, telas e fluxo de trabalho para quem grava, edita, transmite ou publica.</p>
              <a href="/criadores-de-conteudo">Explorar equipamentos <ArrowRight size={16} /></a>
            </div>
          </section>
        </div>
      </article>
      {footer}
    </main>
  );
}



